<template>
	<div class="tw-gap-2 tw-flex tw-flex-auto tw-flex-col required-skills">
		<p-card>
			<div class="tw-px-8 relative">
				<job-complete-validator-tag :jobs="selectedJobs" :job-validator="jobSkillStepValidator" />
				<p-items-carousel
					:items="selectedJobs"
					:per-view="4"
					control-mx="-70px"
					:breakpointPerView="{
						1080: {
							perView: 3,
						},
					}"
				>
					<template slot="default" slot-scope="{ item: job }">
						<job-card-selection
							class="tw-leading-text"
							style="height: 80px"
							:text="job.name"
							:focused="isFocusedJob(job)"
							:checked="jobSkillIsFullyCompleted(job, stepConfig.minSelectedItems)"
							@click="focusJob(job)"
						>
						</job-card-selection>
					</template>
				</p-items-carousel>
			</div>
		</p-card>
		<p-card class="tw-flex-auto tw-flex tw-flex-col tw-justify-between">
			<div class="skills-content">
				<div class="tw-flex tw-justify-between tw-items-center tw-mb-6">
					<p class="tw-text-app-color-primary text-lg">
						{{ CHOOSE_SKILLS_MESSAGE() }}
					</p>
					<p-button
						v-if="!jobIsSkillCompleted(focusedJob, stepConfig.minSelectedItems)"
						type="primary"
						@click="chooseSkills()"
					>
						Escoger {{ stepConfig.stepType }}
					</p-button>
				</div>
				<skill-list
					:skills="propOr([], 'skills', focusedJob)"
					class="skill-list"
					@skillItemRemoveClicked="handleRemoveSkillItem($event)"
					@chooseLevelClicked="handleChooseLevelSkillItem($event)"
				></skill-list>
			</div>
			<div>
				<p-horizontal-divider />
				<div class="tw-text-center">
					<p-button @click="handlePrevStep()" type="primary" class="tw-px-12">
						Atrás
					</p-button>
					<p-button
						:disabled="!stepIsCompleted.isComplete"
						@click="handleNextStep()"
						type="secondary"
					>
						Siguiente
					</p-button>
				</div>
			</div>
			<skill-selection-dialog
				ref="skillSelectionDialog"
				@onSave="handleSelectedSkills($event)"
				:step-config="stepConfig"
				:skills="skillData"
				:selected-skills="propOr([], 'skills', focusedJob)"
			/>
			<skill-level-selector-dialog
				ref="skillLevelSelectionDialog"
				:skill="focusedSkill"
				@onLevelSelected="handleSkillLevelSelection($event)"
			/>
		</p-card>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { assoc, equals, findIndex, head, isEmpty, pathOr, prop, propEq, propOr } from 'ramda';
import PCard from '../../../shared/components/ui/Card/p-card';
import PButton from '../../../shared/components/ui/p-button';
import PHorizontalDivider from '../../../shared/components/ui/p-horizontal-divider';
import SkillList from './components/skill-list';
import PItemsCarousel from '../../../shared/components/content/p-items-corousel';
import JobCardSelection from './components/job-card-selection';
import SkillSelectionDialog from './components/skill-selection-dialog';
import { transformSkillData, tseNamespace } from '../../utils';
import { THREE_SIXTY_EVALUATION_NAMESPACE } from '../../store/store.constant';
import SkillLevelSelectorDialog from './components/skill-level-selector-dialog';
import { updateElementInList, removeElementInList } from '../../../shared/utils/listElements';
import { jobSkillIsFullyCompleted, jobIsSkillCompleted } from './required-skills.utils';
import JobCompleteValidatorTag from '../../commons/job-complete-validator-tag';
import { tseStepIsCompleted } from '../../utils/tse.utils';
import { CHOOSE_SKILLS_MESSAGE } from '../../constants/messages.constants';

export default {
	name: 'RequiredSkills',
	components: {
		JobCompleteValidatorTag,
		SkillLevelSelectorDialog,
		SkillSelectionDialog,
		JobCardSelection,
		PItemsCarousel,
		SkillList,
		PHorizontalDivider,
		PButton,
		PCard,
	},
	props: {
		stepConfig: {
			default: () => ({
				stepType: '-',
				maxSelectedItems: 1,
				minSelectedItems: 1,
				typeKey: '',
				stepNodeName: '',
			}),
		},
	},
	async mounted() {
		const response = await this.getDataStep(this.stepConfig.typeKey);
		this.skillData = transformSkillData(response);
	},
	data() {
		return {
			focusedJob: {},
			skillData: [],
			focusedSkill: {},
		};
	},
	computed: {
		...mapState(THREE_SIXTY_EVALUATION_NAMESPACE, {
			selectedJobs: pathOr([], ['evaluationFlow', 'selectedJobs']),
		}),
		stepIsCompleted() {
			return tseStepIsCompleted(this.jobSkillStepValidator, this.selectedJobs);
		},
	},
	methods: {
		CHOOSE_SKILLS_MESSAGE() {
			return CHOOSE_SKILLS_MESSAGE;
		},
		propOr,
		jobSkillIsFullyCompleted,
		jobIsSkillCompleted,
		jobSkillStepValidator(j) {
			return jobSkillIsFullyCompleted(j, this.stepConfig.minSelectedItems);
		},
		isFocusedJob(job) {
			return equals(prop('id', job), prop('id', this.focusedJob));
		},
		focusJob(job) {
			this.focusedJob = job;
		},
		chooseSkills() {
			this.$refs.skillSelectionDialog.open();
		},
		handleSelectedSkills(selectedSkills) {
			const updatedJob = assoc('skills', selectedSkills, this.focusedJob);
			this.$store.dispatch(tseNamespace('updateSelectedJob'), updatedJob);
			this.$refs.skillSelectionDialog.close();
		},
		handleNextStep() {
			this.$store.dispatch(tseNamespace('nextStep'));
		},
		handlePrevStep() {
			this.$store.dispatch(tseNamespace('prevStep'));
		},
		async getDataStep(typeKey) {
			const { data } = await this.$http.get(`/hierarchies/${typeKey}`);
			return data;
		},
		handleRemoveSkillItem(skillItem) {
			const updatedSkills = removeElementInList(skillItem, this.focusedJob.skills);
			this.$store.dispatch(
				tseNamespace('updateSelectedJob'),
				assoc('skills', updatedSkills, this.focusedJob),
			);
		},
		handleChooseLevelSkillItem(skillItem) {
			this.focusedSkill = skillItem;
			this.$refs.skillLevelSelectionDialog.open();
		},
		handleSkillLevelSelection(skillLevelSelected) {
			this.focusedSkill = {
				...this.focusedSkill,
				selectedLevel: skillLevelSelected,
				selectedQuestions: [], // resetting questions config
			};
			const updatedSkills = updateElementInList(this.focusedSkill, this.focusedJob.skills);
			this.$store.dispatch(tseNamespace('updateSelectedJob'), {
				...this.focusedJob,
				skills: updatedSkills,
				questionsType: undefined, // resetting questions type
			});
		},
	},
	watch: {
		selectedJobs: {
			handler() {
				if (isEmpty(this.focusedJob) && !!this.selectedJobs) {
					this.focusedJob = head(this.selectedJobs);
				}
				if (!isEmpty(this.focusedJob) && !!this.selectedJobs) {
					const focusedIndex = findIndex(
						propEq('id', prop('id', this.focusedJob)),
						this.selectedJobs,
					);
					this.focusedJob = prop(focusedIndex, this.selectedJobs);
				}
			},
			immediate: true,
		},
	},
};
</script>

<style lang="scss" scoped>
.required-skills {
	.skill-list {
		overflow: auto;
		height: 40vh;
	}
}
</style>



// WEBPACK FOOTER //
// src/modules/three-sixty-evaluation/components/second-step/required-skills.vue